import { useNavigate } from "react-router-dom";
import { Sparkles, X, ArrowUpRight } from "lucide-react";

/** Modal shown when a tier-gated action returns 402.
 *
 *  Props:
 *   - open: boolean
 *   - onClose: fn
 *   - feature: short name of the locked feature, e.g. "AI compliance reports"
 *   - message: optional override for the 402 detail message from the API
 */
const UpgradePrompt = ({ open, onClose, feature = "this feature", message = "" }) => {
  const navigate = useNavigate();
  if (!open) return null;
  return (
    <div
      data-testid="upgrade-prompt-overlay"
      className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid="upgrade-prompt-dialog"
        className="w-full max-w-md bg-white border-2 border-slate-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-[#1E1B4B] to-[#0F0E2E] text-white px-6 py-5 flex items-start gap-3">
          <div className="h-10 w-10 bg-teal-500/20 flex items-center justify-center flex-shrink-0">
            <Sparkles size={22} strokeWidth={2.5} className="text-teal-300" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-teal-300">Pro feature</div>
            <h3 className="text-lg font-black mt-1">Upgrade to unlock {feature}</h3>
          </div>
          <button
            data-testid="upgrade-prompt-close"
            onClick={onClose}
            className="text-white/70 hover:text-white"
            aria-label="Close"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-slate-700 leading-relaxed">
            {message ||
              `Your free trial has ended. Upgrade to Pro to use ${feature} and keep your team running at full speed.`}
          </p>
          <ul className="text-sm text-slate-700 space-y-1.5">
            <li className="flex gap-2"><span className="text-teal-600 font-black">✓</span> Unlimited AI compliance reports</li>
            <li className="flex gap-2"><span className="text-teal-600 font-black">✓</span> Customer portal &amp; shareable reports</li>
            <li className="flex gap-2"><span className="text-teal-600 font-black">✓</span> Branded PDFs + Notice of Non-compliance</li>
            <li className="flex gap-2"><span className="text-teal-600 font-black">✓</span> Cancel anytime</li>
          </ul>
          <div className="flex gap-2 pt-2">
            <button
              data-testid="upgrade-prompt-cta"
              onClick={() => { onClose(); navigate("/pricing"); }}
              className="flex-1 h-11 inline-flex items-center justify-center gap-2 bg-[#1E1B4B] text-white font-bold text-sm hover:bg-[#0F0E2E]"
            >
              See plans <ArrowUpRight size={16} strokeWidth={2.5} />
            </button>
            <button
              data-testid="upgrade-prompt-later"
              onClick={onClose}
              className="h-11 px-4 inline-flex items-center justify-center bg-white border-2 border-slate-300 text-slate-700 font-bold text-sm hover:border-slate-900"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;
